// mappings.js - Funciones auxiliares para manejar los códigos de empresa y delegación

/**
 * Mapeo de nombres de empresas a códigos
 * @param {string} nombreEmpresa - Nombre de la empresa
 * @returns {string} - Código de empresa
 */
export const getEmpresaCode = (nombreEmpresa) => {
  if (!nombreEmpresa) return '0001'; // Valor por defecto
  
  // Mapeo ampliado de nombres a códigos
  const empresaMap = {
    'SEGURIDAD INTEGRAL SECOEX': '0001',
    'PRESTACIONES AUXILIARES VALENCIANAS': '0005',
    'SERV. Y FUNCIONES AUX. MANCHEGOS': '0006',
    'SCU PRACOM ANDALUCIA': '0008',
    'PRACON CENTRO DE EMPLEO MADRID': '0013',
    'TELIMAN SERVICIOS COMPLEMENTARIOS': '0016',
    'PRACON SERVIEXT': '0020',
    'BEPART INNOVA': '0040',
    
    // Versiones abreviadas para mejor compatibilidad
    'SECOEX': '0001',
    'PAV': '0005',
    'SFAM': '0006',
    'PRACOM ANDALUCIA': '0008',
    'PRACON MADRID': '0013',
    'TELIMAN': '0016',
    'SERVIEXT': '0020',
    
    // Valores de respaldo
    'EMPRESA PRINCIPAL': '0001',
    'EMPRESA SECUNDARIA': '0002'
  };
  
  // Normalizar el nombre (mayúsculas y sin espacios al principio/final)
  const nombreNormalizado = nombreEmpresa.trim().toUpperCase();
  
  // Buscar código por nombre exacto
  if (empresaMap[nombreNormalizado]) {
    return empresaMap[nombreNormalizado];
  }
  
  // Si no se encuentra, verificar si hay coincidencias parciales
  for (const [nombre, codigo] of Object.entries(empresaMap)) {
    if (nombreNormalizado.includes(nombre) || nombre.includes(nombreNormalizado)) {
      return codigo;
    }
  }
  
  // Búsqueda por palabras clave específicas para cada empresa
  if (nombreNormalizado.includes('SECOEX')) return '0001';
  if (nombreNormalizado.includes('VALENCIAN')) return '0005';
  if (nombreNormalizado.includes('MANCHEG')) return '0006';
  if (nombreNormalizado.includes('ANDALUCIA') || nombreNormalizado.includes('PRACOM')) return '0008';
  if (nombreNormalizado.includes('MADRID') || nombreNormalizado.includes('PRACON')) return '0013';
  if (nombreNormalizado.includes('TELIMAN')) return '0016';
  if (nombreNormalizado.includes('SERVIEXT')) return '0020';
  
  // Si no hay coincidencias, devolver código por defecto
  return '0001';
};

/**
 * Mapeo de nombres de provincias a códigos de delegación
 * @param {string} provincia - Nombre de la provincia
 * @returns {string} - Código de delegación
 */
export const getDelegacionCode = (provincia) => {
  if (!provincia) return '06'; // Valor por defecto
  
  // Mapeo completo de provincias a códigos de delegación
  const delegacionMap = {
    // Provincias principales
    'MADRID': '01',
    'BARCELONA': '02',
    'VALENCIA': '03',
    'SEVILLA': '04',
    'MALAGA': '05',
    'BADAJOZ': '06',
    'ZARAGOZA': '07',
    'BILBAO': '08',
    'VALLADOLID': '09',
    'GRANADA': '10',
    'CÓRDOBA': '11',
    'JAÉN': '12',
    'ALMERÍA': '13',
    'HUELVA': '14',
    'CÁDIZ': '15',
    'CÁCERES': '16',
    'TOLEDO': '17',
    'CIUDAD REAL': '18',
    'ALBACETE': '19',
    'CUENCA': '20',
    'GUADALAJARA': '21',
    'ÁVILA': '22',
    'SALAMANCA': '23',
    'ZAMORA': '24',
    'LEÓN': '25',
    'PALENCIA': '26',
    'BURGOS': '27',
    'SORIA': '28',
    'SEGOVIA': '29',
    'VALLADOLID': '30',
    'LA RIOJA': '31',
    'NAVARRA': '32',
    'ASTURIAS': '33',
    'CANTABRIA': '34',
    'VITORIA': '35',
    'PONTEVEDRA': '36',
    'LA CORUÑA': '37',
    'TENERIFE': '38',
    'LAS PALMAS': '39',
    'ALICANTE': '40',
    'CASTELLÓN': '41',
    'TARRAGONA': '42',
    'GERONA': '43',
    'LÉRIDA': '44',
    'HUESCA': '45',
    'TERUEL': '46',
    'VALLADOLID': '47',
    'VIZCAYA': '48',
    'GUIPÚZCOA': '49',
    'ZARAGOZA': '50',
    
    // Comunidades Autónomas
    'ANDALUCÍA': '04',
    'ARAGÓN': '50',
    'ASTURIAS': '33',
    'CANTABRIA': '34',
    'CASTILLA Y LEÓN': '30',
    'CASTILLA-LA MANCHA': '17',
    'CATALUÑA': '02',
    'EXTREMADURA': '06',
    'GALICIA': '36',
    'ISLAS BALEARES': '07',
    'ISLAS CANARIAS': '38',
    'LA RIOJA': '31',
    'MADRID': '01',
    'MURCIA': '30',
    'NAVARRA': '32',
    'PAÍS VASCO': '48',
    'VALENCIA': '03',
    
    // Variaciones comunes
    'BADAJOZ': '06',
    'CACERES': '16',
    'CORDOBA': '11',
    'JAEN': '12',
    'ALMERIA': '13',
    'CADIZ': '15',
    'TOLEDO': '17',
    'CIUDAD REAL': '18',
    'ALBACETE': '19',
    'GUADALAJARA': '21',
    'AVILA': '22',
    'SALAMANCA': '23',
    'ZAMORA': '24',
    'LEON': '25',
    'PALENCIA': '26',
    'BURGOS': '27',
    'SORIA': '28',
    'SEGOVIA': '29',
    'VALLADOLID': '30',
    'LA RIOJA': '31',
    'NAVARRA': '32',
    'ASTURIAS': '33',
    'CANTABRIA': '34',
    'VITORIA': '35',
    'PONTEVEDRA': '36',
    'LA CORUNA': '37',
    'TENERIFE': '38',
    'LAS PALMAS': '39',
    'ALICANTE': '40',
    'CASTELLON': '41',
    'TARRAGONA': '42',
    'GERONA': '43',
    'LERIDA': '44',
    'HUESCA': '45',
    'TERUEL': '46',
    'VALLADOLID': '47',
    'VIZCAYA': '48',
    'GUIPUZCOA': '49',
    'ZARAGOZA': '50'
  };
  
  // Normalizar la provincia (mayúsculas y sin espacios al principio/final)
  const provinciaNormalizada = provincia.trim().toUpperCase();
  
  console.log(`Mapeando provincia: "${provincia}" -> "${provinciaNormalizada}"`);
  
  // Buscar código por nombre exacto
  if (delegacionMap[provinciaNormalizada]) {
    console.log(`✅ Mapeo exacto encontrado: ${provinciaNormalizada} -> ${delegacionMap[provinciaNormalizada]}`);
    return delegacionMap[provinciaNormalizada];
  }
  
  // Si no se encuentra, verificar si hay coincidencias parciales
  for (const [nombre, codigo] of Object.entries(delegacionMap)) {
    if (provinciaNormalizada.includes(nombre) || nombre.includes(provinciaNormalizada)) {
      console.log(`✅ Mapeo parcial encontrado: ${provinciaNormalizada} (contiene ${nombre}) -> ${codigo}`);
      return codigo;
    }
  }
  
  // Búsqueda por palabras clave específicas
  if (provinciaNormalizada.includes('BADAJOZ')) return '06';
  if (provinciaNormalizada.includes('CACERES') || provinciaNormalizada.includes('CÁCERES')) return '16';
  if (provinciaNormalizada.includes('MADRID')) return '01';
  if (provinciaNormalizada.includes('BARCELONA')) return '02';
  if (provinciaNormalizada.includes('VALENCIA')) return '03';
  if (provinciaNormalizada.includes('SEVILLA')) return '04';
  if (provinciaNormalizada.includes('MALAGA') || provinciaNormalizada.includes('MÁLAGA')) return '05';
  if (provinciaNormalizada.includes('ZARAGOZA')) return '50';
  if (provinciaNormalizada.includes('BILBAO')) return '08';
  if (provinciaNormalizada.includes('VALLADOLID')) return '30';
  if (provinciaNormalizada.includes('GRANADA')) return '10';
  if (provinciaNormalizada.includes('CORDOBA') || provinciaNormalizada.includes('CÓRDOBA')) return '11';
  if (provinciaNormalizada.includes('JAEN') || provinciaNormalizada.includes('JAÉN')) return '12';
  if (provinciaNormalizada.includes('ALMERIA') || provinciaNormalizada.includes('ALMERÍA')) return '13';
  if (provinciaNormalizada.includes('HUELVA')) return '14';
  if (provinciaNormalizada.includes('CADIZ') || provinciaNormalizada.includes('CÁDIZ')) return '15';
  if (provinciaNormalizada.includes('TOLEDO')) return '17';
  if (provinciaNormalizada.includes('CIUDAD REAL')) return '18';
  if (provinciaNormalizada.includes('ALBACETE')) return '19';
  if (provinciaNormalizada.includes('CUENCA')) return '20';
  if (provinciaNormalizada.includes('GUADALAJARA')) return '21';
  if (provinciaNormalizada.includes('AVILA') || provinciaNormalizada.includes('ÁVILA')) return '22';
  if (provinciaNormalizada.includes('SALAMANCA')) return '23';
  if (provinciaNormalizada.includes('ZAMORA')) return '24';
  if (provinciaNormalizada.includes('LEON') || provinciaNormalizada.includes('LEÓN')) return '25';
  if (provinciaNormalizada.includes('PALENCIA')) return '26';
  if (provinciaNormalizada.includes('BURGOS')) return '27';
  if (provinciaNormalizada.includes('SORIA')) return '28';
  if (provinciaNormalizada.includes('SEGOVIA')) return '29';
  if (provinciaNormalizada.includes('LA RIOJA')) return '31';
  if (provinciaNormalizada.includes('NAVARRA')) return '32';
  if (provinciaNormalizada.includes('ASTURIAS')) return '33';
  if (provinciaNormalizada.includes('CANTABRIA')) return '34';
  if (provinciaNormalizada.includes('VITORIA')) return '35';
  if (provinciaNormalizada.includes('PONTEVEDRA')) return '36';
  if (provinciaNormalizada.includes('LA CORUNA') || provinciaNormalizada.includes('LA CORUÑA')) return '37';
  if (provinciaNormalizada.includes('TENERIFE')) return '38';
  if (provinciaNormalizada.includes('LAS PALMAS')) return '39';
  if (provinciaNormalizada.includes('ALICANTE')) return '40';
  if (provinciaNormalizada.includes('CASTELLON') || provinciaNormalizada.includes('CASTELLÓN')) return '41';
  if (provinciaNormalizada.includes('TARRAGONA')) return '42';
  if (provinciaNormalizada.includes('GERONA')) return '43';
  if (provinciaNormalizada.includes('LERIDA') || provinciaNormalizada.includes('LÉRIDA')) return '44';
  if (provinciaNormalizada.includes('HUESCA')) return '45';
  if (provinciaNormalizada.includes('TERUEL')) return '46';
  if (provinciaNormalizada.includes('VIZCAYA')) return '48';
  if (provinciaNormalizada.includes('GUIPUZCOA') || provinciaNormalizada.includes('GUIPÚZCOA')) return '49';
  
  console.log(`⚠️ No se encontró mapeo para provincia: "${provinciaNormalizada}", usando valor por defecto: 06`);
  return '06';
};

/**
 * Extrae el contrato y la delegación de una cadena de localización
 * @param {string} localizacion - Cadena con formato "XXXX/XX" donde XXXX es contrato y XX es delegación
 * @returns {Object} - Objeto con las partes extraídas {contrato, delegacion, servicio}
 */
export const extractLocalizacionParts = (localizacion) => {
  if (!localizacion) {
    return {
      contrato: '',
      delegacion: '',
      servicio: '01'
    };
  }
  
  // Formato esperado: "XXXX/XX" donde XXXX es contrato y XX es delegación
  const partes = localizacion.split('/');
  
  // Si tiene el formato correcto "XXXX/XX"
  if (partes.length === 2) {
    return {
      contrato: partes[0],   // Primer valor es contrato (XXXX)
      delegacion: partes[1], // Segundo valor es delegación (XX)
      servicio: '01'         // Valor por defecto para servicio
    };
  }
  
  // Si es solo el número de contrato
  if (partes.length === 1) {
    return {
      contrato: partes[0],
      delegacion: '', // Vacío para permitir que se use el valor de la API
      servicio: '01'
    };
  }
  
  // Si tiene también código de servicio "XXXX/XX/XX"
  if (partes.length >= 3) {
    return {
      contrato: partes[0],
      delegacion: partes[1],
      servicio: partes[2]
    };
  }
  
  // Si el formato no coincide con lo esperado, devolver valores vacíos
  return {
    contrato: '',
    delegacion: '',
    servicio: '01'
  };
};

/**
 * Formatea un código de servicio para asegurar que tiene dos dígitos
 * @param {string} codigo - Código de servicio
 * @returns {string} - Código formateado
 */
export const formatServiceCode = (codigo) => {
  if (!codigo) return '01';
  
  // Asegurar que el código tiene al menos 2 dígitos
  return codigo.toString().padStart(2, '0');
};

export default {
  getEmpresaCode,
  getDelegacionCode,
  extractLocalizacionParts,
  formatServiceCode
};